<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useNotification } from '~/composables/useNotification';
import { useNotificationsStore } from '~/store/notifications';
import type { Notification } from '~/types/Notification';
import { AppButton } from './base';

const router = useRouter();
const { transformNotification, updateNotification } = useNotification();

// Get notifications from store
const { closeDrawer, updateNotification: storeUpdateNotification } = useNotificationsStore();
const { notifications, pagination } = storeToRefs(useNotificationsStore());

// Methods
const closeModal = () => {
  closeDrawer();
};

const handleNotificationClick = async (notification: Notification) => {
  // Mark as read if it was unread
  if (!notification.read) {
    await updateNotification(notification.id, { read: true });
    storeUpdateNotification(notification.id, { read: true });
  }

  // Navigate to related content if available
  if (notification.job_id && notification.type === 'tender-created') {
    closeModal();
    router.push(`/my-listings`);
  }
};

const handleViewAll = () => {
  closeModal();
  router.push('/notifications');
};
</script>

<template>
  <v-card class="notifications-modal">
    <!-- Header -->
    <div class="modal-header">
      <h2 class="modal-title">Notifications ({{ pagination.total }})</h2>
      <v-btn icon="mdi-close" variant="text" size="small" @click="closeModal" class="close-btn" />
    </div>

    <!-- Notifications List or Empty State -->
    <div class="notifications-content">
      <!-- Show notifications when available -->
      <div v-if="notifications.length > 0" class="notifications-list">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="notification-item"
          :class="{ 'is-read': notification.read }"
          @click="handleNotificationClick(notification)"
        >
          <!-- Notification Content -->
          <div class="notification-content">
            <div class="notification-text" v-html="transformNotification(notification).message" />
            <div class="notification-timestamp">
              {{ $getFormattedTimestamp(notification.created_at || '') }}
            </div>
          </div>

          <!-- Notification Dot (shown on right side for unread notifications) -->
          <div v-if="!notification.read" class="notification-dot" />
        </div>
      </div>

      <!-- Show empty state when no notifications -->
      <div v-else class="empty-state">
        <div class="empty-state-icon">
          <v-icon size="64" color="#80808e">mdi-bell-outline</v-icon>
        </div>
        <div class="empty-state-content">
          <h3 class="empty-state-title">No notifications</h3>
          <p class="empty-state-description">You're all caught up! We'll notify you when you have new updates.</p>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="modal-footer">
      <AppButton variant="outlined" @click="handleViewAll" class="view-all-btn"> View All </AppButton>
    </div>
  </v-card>
</template>

<style lang="scss" scoped>
.notifications-modal {
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0px 16px 32px 0px rgba(33, 33, 35, 0.1);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px 32px 0;
  gap: 8px;
}

.modal-title {
  font-family: 'Big Shoulders Display', cursive;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.14;
  color: #01011d;
  margin: 0;
}

.close-btn {
  border-radius: 64px;
  padding: 4px;
  border: 1px solid #e6e6e8;
}

.notifications-content {
  flex: 1;
  padding: 32px 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  max-width: 588px;
}

.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.notification-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid #e6e6e8;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &.is-read {
    opacity: 0.7;
  }
}

.notification-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.notification-text {
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  // font-weight: 400;
  // line-height: 1.14;
  color: #01011d;
  word-break: break-word;
}

.notification-dot {
  width: 8px;
  height: 8px;
  background-color: #f10021;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: 8px;
}

.notification-timestamp {
  font-family: 'Noto Sans', sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.45;
  color: #80808e;
}

.modal-footer {
  padding: 0 32px 32px;
}

.view-all-btn {
  height: 40px;
  border-radius: 64px;
  border: 1px solid currentColor;
  text-transform: none;
  letter-spacing: normal;

  :deep(.v-btn__content) {
    font-family: 'Noto Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.025em;
    gap: 8px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 32px;
  min-height: 200px;
}

.empty-state-icon {
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.empty-state-title {
  font-family: 'Big Shoulders Display', cursive;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: #01011d;
  margin: 0;
}

.empty-state-description {
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #80808e;
  margin: 0;
  max-width: 280px;
}

// Mobile responsive
@media (max-width: 600px) {
  .notifications-modal {
    margin: 16px;
    max-height: 90vh;
  }

  .modal-header {
    padding: 24px 24px 0;
  }

  .notifications-content {
    padding: 24px 24px 32px;
  }

  .notifications-list {
    gap: 16px;
  }

  .modal-footer {
    padding: 0 24px 24px;
  }

  .empty-state {
    padding: 32px 24px;
    min-height: 160px;
  }

  .empty-state-title {
    font-size: 20px;
  }

  .empty-state-description {
    font-size: 13px;
    max-width: 240px;
  }
}
</style>
